import React, { useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Stethoscope, Loader2, Check, X, ShieldCheck, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const PW_RULES = [
    { id: "len", label: "Mínimo 8 caracteres", test: (s) => s.length >= 8 },
    { id: "upper", label: "Uma letra maiúscula", test: (s) => /[A-Z]/.test(s) },
    { id: "num", label: "Um número", test: (s) => /\d/.test(s) },
    { id: "sym", label: "Um caractere especial", test: (s) => /[^A-Za-z0-9]/.test(s) },
];

export default function ResetPassword() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState(null);

    const pwChecks = useMemo(() => PW_RULES.map((r) => ({ ...r, ok: r.test(password) })), [password]);
    const strength = pwChecks.filter((c) => c.ok).length;
    const matches = password && password === confirm;
    const canSubmit = strength === 4 && matches;

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        if (!canSubmit) { setError("Verifique as regras de senha e a confirmação."); return; }
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: password });
            setDone(true);
            toast.success("Senha redefinida! Você já pode entrar.");
            setTimeout(() => navigate("/login"), 1600);
        } catch (e) { setError(formatApiError(e)); }
        finally { setLoading(false); }
    };

    return (
        <div className="min-h-screen bg-base flex items-center justify-center px-4 py-10">
            <div className="w-full max-w-md">
                <Link to="/" className="flex items-center gap-2 justify-center mb-8">
                    <span className="w-10 h-10 rounded-xl bg-brand text-white flex items-center justify-center">
                        <Stethoscope size={18} />
                    </span>
                    <span className="font-display text-brand text-lg font-semibold">Clínica Fernandes</span>
                </Link>
                <div className="card-earthy">
                    {done ? (
                        <div data-testid="reset-success" className="text-center py-4">
                            <div className="w-14 h-14 mx-auto rounded-full bg-[#E5EFE0] text-[#315B3A] flex items-center justify-center mb-4">
                                <ShieldCheck size={28} />
                            </div>
                            <h2 className="font-display text-2xl text-brand mb-2">Senha redefinida</h2>
                            <p className="text-sm text-muted-clinical">Redirecionando para o login...</p>
                        </div>
                    ) : (
                        <>
                            <h1 className="font-display text-2xl font-medium text-brand mb-1">Nova senha</h1>
                            <p className="text-sm text-muted-clinical mb-6">Defina uma senha forte para sua conta.</p>
                            <form onSubmit={submit} className="space-y-4">
                                <div>
                                    <label className="label-clinical">Nova senha</label>
                                    <input
                                        data-testid="reset-password-input"
                                        type="password"
                                        autoComplete="new-password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        className="input-clinical"
                                        placeholder="Mínimo 8 caracteres"
                                    />
                                    {password && (
                                        <ul className="mt-2 space-y-1 text-xs">
                                            {pwChecks.map((c) => (
                                                <li key={c.id} data-testid={`reset-rule-${c.id}`} className={`flex items-center gap-2 ${c.ok ? "text-[#2A5746]" : "text-muted-clinical"}`}>
                                                    {c.ok ? <Check size={12} /> : <X size={12} />} {c.label}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                                <div>
                                    <label className="label-clinical">Confirmar senha</label>
                                    <input
                                        data-testid="reset-confirm-input"
                                        type="password"
                                        autoComplete="new-password"
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        required
                                        className="input-clinical"
                                        placeholder="Digite novamente"
                                    />
                                    {confirm && (
                                        <p className={`text-xs mt-1 ${matches ? "text-[#2A5746]" : "text-[var(--status-danger)]"}`}>
                                            {matches ? "Senhas coincidem" : "As senhas não coincidem"}
                                        </p>
                                    )}
                                </div>
                                {error && (
                                    <div data-testid="reset-error" className="text-sm text-[var(--status-danger)] bg-[var(--status-danger-bg)] border-l-4 border-[var(--status-danger)] p-3 rounded">{error}</div>
                                )}
                                <button data-testid="reset-submit" type="submit" disabled={loading || !canSubmit} className="btn-primary w-full">
                                    {loading && <Loader2 size={16} className="animate-spin" />} Redefinir senha
                                </button>
                            </form>
                        </>
                    )}
                    <div className="mt-6 text-center text-sm text-muted-clinical">
                        <Link to="/login" className="text-brand font-medium hover:underline inline-flex items-center gap-1">
                            <ArrowLeft size={14} /> Voltar ao login
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
